import { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, ShieldAlert, ScrollText, Plus, Trash2, Building2, Warehouse, Save, X, CalendarDays, Upload, Loader2, AlertTriangle, Wrench, KeyRound, Pencil, Image as ImageIcon, Smartphone, FileDown } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { notifyLogoChanged } from "@/components/BrandLogo";
import { toast } from "sonner";

const ROLES = { super_admin: "Super-admin", owner: "Propriétaire", admin: "Admin", dispatch: "Répartiteur", driver: "Chauffeur" };

const Field = ({ label, ...p }) => (
  <div><Label className="text-xs">{label}</Label><Input className="mt-1 h-9" {...p} /></div>
);

export default function Admin() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [companies, setCompanies] = useState([]);
  const [garages, setGarages] = useState([]);
  const [users, setUsers] = useState([]);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);

  const [nc, setNc] = useState({ name: "", contact: "", phone: "" });
  const [ng, setNg] = useState({ company_id: "", name: "", address: "", city: "" });
  const [nu, setNu] = useState({ name: "", email: "", password: "", role: "dispatch", company_id: "", garage_ids: [] });
  const [newPin, setNewPin] = useState(null);
  const [dlAndroid, setDlAndroid] = useState(false);
  const [edit, setEdit] = useState(null); // {id, name, password, company_id, garage_ids}
  const [calendars, setCalendars] = useState([]);
  const [schools, setSchools] = useState([]);
  const [sel, setSel] = useState(null);
  const [nc2, setNc2] = useState({ name: "", school_id: null, school_year_start: "", school_year_end: "" });
  const [nh, setNh] = useState({ date: "", label: "" });
  const [pdfBusy, setPdfBusy] = useState(false);
  const [reset, setReset] = useState({ password: "", confirm: "" });
  const [resetBusy, setResetBusy] = useState(false);
  const [pwFor, setPwFor] = useState(null);   // {id, email, password}
  const [diag, setDiag] = useState(null);

  const load = () => {
    api.get("/admin/companies").then((r) => {
      setCompanies(r.data);
      if (!isSuper && r.data.length === 1) {
        setNu((p) => ({ ...p, company_id: r.data[0].id }));
        setNg((p) => ({ ...p, company_id: r.data[0].id }));
      }
    }).catch(() => {});
    api.get("/admin/garages").then((r) => setGarages(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/safety-rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/admin/audit").then((r) => setLogs(r.data)).catch(() => {});
    api.get("/auth/bootstrap-status").then((r) => setDiag(r.data)).catch(() => {});
    api.get("/calendars").then((r) => { setCalendars(r.data); if (r.data.length && !sel) setSel(r.data[0].id); }).catch(() => {});
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";
  const garagesOf = (cid) => garages.filter((g) => g.company_id === cid);

  const call = async (fn, msg) => {
    try { const custom = await fn(); toast.success(custom || msg); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const current = calendars.find((c) => c.id === sel) || null;

  const saveCalendar = async (next) => {
    try {
      const { data } = await api.put(`/calendars/${next.id}`, {
        name: next.name, school_id: next.school_id || null,
        school_year_start: next.school_year_start || "", school_year_end: next.school_year_end || "",
        holidays: next.holidays || [],
      });
      setCalendars((p) => p.map((c) => (c.id === data.id ? data : c)));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const importPdf = async (e) => {
    const file = e.target.files?.[0]; if (!file || !current) return;
    setPdfBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/calendars/import-pdf", fd);
      const merged = [...(current.holidays || []), ...data.holidays]
        .filter((h, i, arr) => arr.findIndex((x) => x.date === h.date) === i)
        .sort((a, b) => a.date.localeCompare(b.date));
      await saveCalendar({
        ...current, holidays: merged,
        school_year_start: current.school_year_start || data.year_start || "",
        school_year_end: current.school_year_end || data.year_end || "",
      });
      toast.success(`${data.count} journée(s) sans transport importée(s)${data.school ? ` — ${data.school}` : ""}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPdfBusy(false); e.target.value = ""; }
  };

  const toggleGarage = (state, setState, gid) => {
    const has = (state.garage_ids || []).includes(gid);
    setState({ ...state, garage_ids: has ? state.garage_ids.filter((x) => x !== gid) : [...(state.garage_ids || []), gid] });
  };

  return (
    <div>
      <PageHeader title="Administration" subtitle="Compagnies, garages, répartiteurs, règles et audit" icon={Users} />
      <div className="p-8">
        <Tabs defaultValue="companies">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="companies" data-testid="tab-companies"><Building2 className="w-4 h-4 mr-1" /> Compagnies</TabsTrigger>
            <TabsTrigger value="garages" data-testid="tab-garages"><Warehouse className="w-4 h-4 mr-1" /> Garages</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-1" /> Utilisateurs &amp; mots de passe</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays className="w-4 h-4 mr-1" /> Calendriers scolaires</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules"><ShieldAlert className="w-4 h-4 mr-1" /> Règles de sécurité</TabsTrigger>
            <TabsTrigger value="branding" data-testid="tab-branding"><ImageIcon className="w-4 h-4 mr-1" /> Logo &amp; marque</TabsTrigger>
            {isSuper && <TabsTrigger value="android" data-testid="tab-android"><Smartphone className="w-4 h-4 mr-1" /> Application Android</TabsTrigger>}
            <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench className="w-4 h-4 mr-1" /> Maintenance</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="w-4 h-4 mr-1" /> Audit</TabsTrigger>
          </TabsList>

          {/* ---------- Compagnies ---------- */}
          <TabsContent value="companies">
            <div className="grid md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <Field label="Nom de la compagnie" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} data-testid="company-name" />
              <Field label="Contact" value={nc.contact} onChange={(e) => setNc({ ...nc, contact: e.target.value })} />
              <Field label="Téléphone" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} />
              <Button data-testid="create-company"
                onClick={() => call(async () => { await api.post("/admin/companies", nc); setNc({ name: "", contact: "", phone: "" }); }, "Compagnie créée")}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            <Rows items={companies} testid="company"
              render={(c) => [c.name, c.contact, c.phone, `${garagesOf(c.id).length} garage(s)`]}
              onDel={(c) => call(() => api.delete(`/admin/companies/${c.id}`), "Compagnie supprimée")} />
          </TabsContent>

          {/* ---------- Garages ---------- */}
          <TabsContent value="garages">
            <div className="grid md:grid-cols-5 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <div><Label className="text-xs">Compagnie</Label>
                <Select value={ng.company_id} onValueChange={(v) => setNg({ ...ng, company_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="garage-company"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <Field label="Nom du garage" value={ng.name} onChange={(e) => setNg({ ...ng, name: e.target.value })} data-testid="garage-name" />
              <Field label="Adresse" value={ng.address} onChange={(e) => setNg({ ...ng, address: e.target.value })} data-testid="garage-address" />
              <Field label="Ville" value={ng.city} onChange={(e) => setNg({ ...ng, city: e.target.value })} />
              <Button data-testid="create-garage"
                onClick={() => call(async () => { await api.post("/admin/garages", ng); setNg({ company_id: ng.company_id, name: "", address: "", city: "" }); }, "Garage créé (adresse géocodée)")}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            <Rows items={garages} testid="garage"
              render={(g) => [g.name, companyName(g.company_id), `${g.address} ${g.city}`,
                g.lat ? <span className="font-mono text-xs">{g.lat.toFixed(4)}, {g.lng.toFixed(4)}</span> : <Badge variant="destructive">non géocodé</Badge>]}
              onDel={(g) => call(() => api.delete(`/admin/garages/${g.id}`), "Garage supprimé")} />
          </TabsContent>

          {/* ---------- Répartiteurs ---------- */}
          <TabsContent value="users">
            <div className="p-4 border border-border rounded-lg mb-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Créez ici les accès de connexion : <strong>répartiteur</strong> (une compagnie + plusieurs garages),
                <strong> chauffeur</strong> (un seul garage — sa fiche est créée automatiquement, avec un NIP de 6 chiffres) ou un autre <strong>admin</strong>.
                Pour régénérer le NIP d'un chauffeur existant, utilisez la clé dans Flotte &amp; Écoles → Chauffeurs.
              </p>
              <div className="grid md:grid-cols-5 gap-3 items-end">
                <Field label="Nom" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} data-testid="user-name" />
                {nu.role === "driver" ? (
                  <div className="md:col-span-2 text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2.5" data-testid="driver-pin-notice">
                    Aucun courriel ni mot de passe : un <strong>NIP de 6 chiffres</strong> est généré
                    automatiquement et affiché après la création.
                  </div>
                ) : (
                  <>
                    <Field label="Courriel" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="user-email" />
                    <Field label="Mot de passe" type="password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} data-testid="user-password" />
                  </>
                )}
                <div><Label className="text-xs">Rôle</Label>
                  <Select value={nu.role} onValueChange={(v) => setNu({ ...nu, role: v })}>
                    <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dispatch">Répartiteur</SelectItem>
                      <SelectItem value="driver">Chauffeur</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      {isSuper && <SelectItem value="owner">Propriétaire (client)</SelectItem>}
                    </SelectContent></Select></div>
                <div className={isSuper ? "" : "hidden"}><Label className="text-xs">Compagnie</Label>
                  <Select value={nu.company_id} onValueChange={(v) => setNu({ ...nu, company_id: v, garage_ids: [] })}>
                    <SelectTrigger className="mt-1" data-testid="user-company"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              {nu.role !== "owner" && nu.company_id && (
                <div>
                  <Label className="text-xs">
                    {nu.role === "driver" ? "Garage du chauffeur (un seul)" : "Garages attribués (un répartiteur peut en avoir plusieurs)"}
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {garagesOf(nu.company_id).map((g) => (
                      <button key={g.id}
                        onClick={() => nu.role === "driver" ? setNu({ ...nu, garage_ids: [g.id] }) : toggleGarage(nu, setNu, g.id)}
                        data-testid={`pick-garage-${g.id}`}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${nu.garage_ids.includes(g.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                        {g.name}
                      </button>
                    ))}
                    {!garagesOf(nu.company_id).length && <span className="text-xs text-muted-foreground">Aucun garage pour cette compagnie.</span>}
                  </div>
                </div>
              )}
              <Button data-testid="create-user"
                onClick={() => call(async () => {
                  const payload = nu.role === "driver"
                    ? { name: nu.name, role: "driver", company_id: nu.company_id, garage_ids: nu.garage_ids }
                    : nu;
                  const { data } = await api.post("/admin/users", payload);
                  setNu({ name: "", email: "", password: "", role: nu.role, company_id: nu.company_id, garage_ids: [] });
                  if (data.pin) {
                    setNewPin({ name: data.name, pin: data.pin });
                    return `Chauffeur créé — NIP : ${data.pin}`;
                  }
                }, "Utilisateur créé")}>
                <Plus className="w-4 h-4 mr-1" /> Créer
              </Button>
              {newPin && (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-4" data-testid="new-pin-banner">
                  <div className="text-sm">
                    <div className="font-semibold">{newPin.name}</div>
                    <div className="text-xs text-muted-foreground">NIP de connexion — à remettre au chauffeur</div>
                  </div>
                  <button className="font-mono text-2xl font-bold tracking-widest text-emerald-700 hover:underline"
                    data-testid="new-pin-value"
                    onClick={() => { navigator.clipboard?.writeText(newPin.pin); toast.success("NIP copié"); }}>
                    {newPin.pin}
                  </button>
                  <button onClick={() => setNewPin(null)} data-testid="new-pin-dismiss"
                    className="ml-auto text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                </div>
              )}
            </div>

            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              {users.map((u) => (
                <div key={u.id} className="px-5 py-3 bg-card text-sm" data-testid={`user-row-${u.email}`}>
                  {edit?.id === u.id ? (
                    <div className="space-y-3">
                      <div className="grid md:grid-cols-4 gap-3 items-end">
                        <Field label="Nom" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                        <Field label="Nouveau mot de passe" type="password" placeholder="(inchangé)" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} data-testid="edit-password" />
                        <div><Label className="text-xs">Compagnie</Label>
                          <Select value={edit.company_id || ""} onValueChange={(v) => setEdit({ ...edit, company_id: v, garage_ids: [] })}>
                            <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                            <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                          </Select></div>
                        <div className="flex gap-2">
                          <Button size="sm" data-testid="save-user"
                            onClick={() => call(async () => {
                              const body = { name: edit.name, company_id: edit.company_id, garage_ids: edit.garage_ids };
                              if (edit.password) body.password = edit.password;
                              await api.put(`/admin/users/${u.id}`, body); setEdit(null);
                            }, "Répartiteur mis à jour")}><Save className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEdit(null)}><X className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {edit.company_id && (
                        <div className="flex flex-wrap gap-2">
                          {garagesOf(edit.company_id).map((g) => (
                            <button key={g.id} onClick={() => toggleGarage(edit, setEdit, g.id)}
                              className={`px-3 py-1.5 rounded-full text-xs border ${edit.garage_ids?.includes(g.id) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{g.name}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-3 font-medium">{u.name}</div>
                      <div className="col-span-3">
                        {u.role === "driver"
                          ? (u.pin
                            ? <button className="font-mono text-base font-bold tracking-widest text-primary hover:underline"
                                data-testid={`user-pin-${u.id}`}
                                onClick={() => { navigator.clipboard?.writeText(u.pin); toast.success("NIP copié"); }}>
                                NIP {u.pin}
                              </button>
                            : <span className="text-muted-foreground">aucun NIP</span>)
                          : u.email}
                      </div>
                      <div className="col-span-1"><Badge variant="secondary">{ROLES[u.role] || u.role}</Badge></div>
                      <div className="col-span-2 text-xs text-muted-foreground">{u.company_id ? companyName(u.company_id) : "—"}</div>
                      <div className="col-span-2 text-xs text-muted-foreground">
                        {(u.garage_ids || []).map((id) => garages.find((g) => g.id === id)?.name).filter(Boolean).join(", ") || "tous"}
                      </div>
                      <div className="col-span-1 text-right flex justify-end gap-1">
                        <Button size="sm" variant="ghost" data-testid={`pw-${u.email}`} title="Modifier le mot de passe"
                          onClick={() => setPwFor(pwFor?.id === u.id ? null : { id: u.id, email: u.email, password: "" })}>
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`edit-${u.email}`} title="Modifier le compte"
                          onClick={() => setEdit({ id: u.id, name: u.name || "", password: "", company_id: u.company_id || "", garage_ids: u.garage_ids || [] })}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`delete-${u.email}`}
                          onClick={() => call(() => api.delete(`/admin/users/${u.id}`), "Utilisateur supprimé")}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {pwFor?.id === u.id && (
                    <div className="grid md:grid-cols-3 gap-3 items-end mt-3 pt-3 border-t border-border">
                      <div className="md:col-span-2">
                        <Label className="text-xs">Nouveau mot de passe pour {u.email} (8 caractères minimum)</Label>
                        <Input type="password" className="mt-1 h-9" value={pwFor.password} data-testid={`pw-input-${u.email}`}
                          onChange={(e) => setPwFor({ ...pwFor, password: e.target.value })} />
                      </div>
                      <Button data-testid={`pw-save-${u.email}`} onClick={() => {
                        if ((pwFor.password || "").length < 8) return toast.error("Au moins 8 caractères");
                        call(async () => { await api.put(`/admin/users/${u.id}`, { password: pwFor.password }); setPwFor(null); },
                          `Mot de passe de ${u.email} modifié`);
                      }}>Enregistrer le mot de passe</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ---------- Calendriers scolaires (un par école) ---------- */}
          <TabsContent value="calendar">
            <div className="grid md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-lg mb-4">
              <div><Label className="text-xs">Nom du calendrier</Label>
                <Input className="mt-1 h-9" placeholder="Bedford 2026-2027" value={nc2.name} data-testid="cal-name"
                  onChange={(e) => setNc2({ ...nc2, name: e.target.value })} /></div>
              <div><Label className="text-xs">École (optionnel)</Label>
                <Select value={nc2.school_id || "none"} onValueChange={(v) => setNc2({ ...nc2, school_id: v === "none" ? null : v })}>
                  <SelectTrigger className="mt-1" data-testid="cal-school"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Toutes les écoles (par défaut)</SelectItem>
                    {schools.map((sc) => <SelectItem key={sc.id} value={sc.id}>{sc.name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Début</Label>
                  <Input type="date" className="mt-1 h-9" value={nc2.school_year_start} data-testid="cal-start"
                    onChange={(e) => setNc2({ ...nc2, school_year_start: e.target.value })} /></div>
                <div><Label className="text-xs">Fin</Label>
                  <Input type="date" className="mt-1 h-9" value={nc2.school_year_end} data-testid="cal-end"
                    onChange={(e) => setNc2({ ...nc2, school_year_end: e.target.value })} /></div>
              </div>
              <Button data-testid="create-calendar" onClick={() => {
                if (!nc2.name) return toast.error("Donnez un nom au calendrier");
                call(async () => { const { data } = await api.post("/calendars", { ...nc2, holidays: [] }); setSel(data.id); setNc2({ name: "", school_id: null, school_year_start: "", school_year_end: "" }); }, "Calendrier créé");
              }}><Plus className="w-4 h-4 mr-1" /> Ajouter un calendrier</Button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="calendar-list">
              {!calendars.length && <span className="text-sm text-muted-foreground">Aucun calendrier. Créez-en un par école — chaque école a ses propres congés.</span>}
              {calendars.map((c) => (
                <button key={c.id} onClick={() => setSel(c.id)} data-testid={`cal-tab-${c.id}`}
                  className={`px-3 py-2 rounded-lg text-sm border text-left transition-colors ${sel === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
                  <div className="font-semibold">{c.name}</div>
                  <div className={`text-[11px] ${sel === c.id ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                    {c.school_name || "toutes les écoles"} · {(c.holidays || []).length} journée(s) sans transport
                  </div>
                </button>
              ))}
            </div>

            {current && (
              <>
                <div className="grid md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-lg mb-4">
                  <div className="text-sm">
                    <div className="font-display font-semibold">{current.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {current.school_name || "toutes les écoles"} · {current.school_year_start || "?"} → {current.school_year_end || "?"}
                    </div>
                  </div>
                  <label className="h-9 rounded-md border border-dashed border-border flex items-center justify-center gap-2 text-sm cursor-pointer hover:bg-secondary" data-testid="calendar-pdf-label">
                    <input type="file" accept="application/pdf" className="hidden" onChange={importPdf} data-testid="calendar-pdf" />
                    {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {pdfBusy ? "Lecture du PDF…" : "Importer ce calendrier (PDF)"}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" className="h-9" value={current.school_year_start || ""} data-testid="cal-edit-start"
                      onChange={(e) => saveCalendar({ ...current, school_year_start: e.target.value })} />
                    <Input type="date" className="h-9" value={current.school_year_end || ""} data-testid="cal-edit-end"
                      onChange={(e) => saveCalendar({ ...current, school_year_end: e.target.value })} />
                  </div>
                  <Button variant="outline" data-testid="delete-calendar"
                    onClick={() => call(async () => { await api.delete(`/calendars/${current.id}`); setSel(null); }, "Calendrier supprimé")}>
                    <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                  </Button>
                </div>

                <div className="grid md:grid-cols-4 gap-3 items-end p-4 border border-border rounded-lg mb-4">
                  <div><Label className="text-xs">Date sans transport</Label>
                    <Input type="date" className="mt-1 h-9" value={nh.date} data-testid="holiday-date"
                      onChange={(e) => setNh({ ...nh, date: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label className="text-xs">Motif</Label>
                    <Input className="mt-1 h-9" placeholder="Journée pédagogique" value={nh.label} data-testid="holiday-label"
                      onChange={(e) => setNh({ ...nh, label: e.target.value })} /></div>
                  <Button data-testid="add-holiday" onClick={() => {
                    if (!nh.date) return toast.error("Choisissez une date");
                    saveCalendar({ ...current, holidays: [...(current.holidays || []), { ...nh, type: "conge" }] });
                    setNh({ date: "", label: "" });
                  }}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
                </div>

                <div className="border border-border rounded-lg divide-y divide-border overflow-hidden max-h-[45vh] overflow-y-auto" data-testid="holiday-list">
                  {!(current.holidays || []).length && <div className="p-6 text-sm text-muted-foreground">Aucune journée sans transport. Importez le calendrier en PDF ou ajoutez les dates à la main.</div>}
                  {(current.holidays || []).map((h, i) => (
                    <div key={h.date + i} className="px-5 py-2.5 grid grid-cols-12 gap-3 items-center text-sm bg-card" data-testid={`holiday-row-${i}`}>
                      <div className="col-span-3 font-mono">{h.date}</div>
                      <div className="col-span-6">{h.label || "—"}</div>
                      <div className="col-span-2"><Badge variant="outline">{h.type}</Badge></div>
                      <div className="col-span-1 text-right">
                        <Button size="sm" variant="ghost" onClick={() => saveCalendar({ ...current, holidays: current.holidays.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------- Logo & marque ---------- */}
          <TabsContent value="branding">
            <div className="grid md:grid-cols-2 gap-4">
              <LogoCard scope="company" title="Logo de la compagnie"
                hint="Affiché dans le portail, l'app chauffeur et en en-tête des rapports PDF de paie." />
              {isSuper && (
                <LogoCard scope="platform" title="Logo de la plateforme (Innova-Solution)"
                  hint="Affiché sur l'écran de connexion et utilisé par défaut pour les compagnies sans logo." />
              )}
            </div>
          </TabsContent>

          {/* ---------- Application Android ---------- */}
          {isSuper && (
            <TabsContent value="android">
              <div className="max-w-2xl border border-border rounded-lg p-6 bg-card" data-testid="android-card">
                <div className="font-display font-semibold text-lg">Application Android des chauffeurs</div>
                <p className="text-sm text-muted-foreground mt-2">
                  Projet Android Studio complet (Kotlin) qui transforme l'interface chauffeur en véritable
                  application installable : <strong>GPS actif écran éteint</strong>, micro du talkie-walkie
                  autorisé, ouverture de Google Maps dans l'application dédiée, plein écran sans barre de navigateur.
                </p>
                <ol className="text-sm mt-4 space-y-1.5 list-decimal list-inside text-slate-600">
                  <li>Téléchargez l'archive et décompressez-la.</li>
                  <li>Android Studio → <em>Open</em> → choisissez le dossier <code>InnovaBusChauffeur</code>.</li>
                  <li>Ouvrez <code>app/src/main/res/values/strings.xml</code> et mettez votre domaine dans <code>app_url</code>.</li>
                  <li><em>Build → Generate Signed Bundle / APK → APK</em>, puis distribuez le fichier aux chauffeurs.</li>
                </ol>
                <p className="text-xs text-muted-foreground mt-4">
                  Le contenu venant du site web, une mise à jour du logiciel ne vous oblige pas à republier l'application.
                  Le fichier <code>README.md</code> inclus détaille les réglages du téléphone (« Toujours autoriser »
                  la localisation, exclusion de l'optimisation de batterie).
                </p>
                <Button className="mt-5" data-testid="download-android" disabled={dlAndroid}
                  onClick={async () => {
                    setDlAndroid(true);
                    try {
                      const r = await api.get("/admin/android-source", { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url; a.download = "InnovaBusChauffeur-android.zip"; a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Archive téléchargée");
                    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                    finally { setDlAndroid(false); }
                  }}>
                  {dlAndroid ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                  Télécharger le projet Android (.zip)
                </Button>
              </div>
            </TabsContent>
          )}

          {/* ---------- Maintenance ---------- */}
          <TabsContent value="maintenance">
            {diag && (
              <div className="rounded-lg border border-border bg-card p-5 mb-4" data-testid="diagnostic-card">
                <h3 className="font-display font-semibold mb-3">Diagnostic de cet environnement</h3>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <Row k="Base de données" v={diag.db_name} />
                  <Row k="Comptes utilisateurs" v={String(diag.users_total)} />
                  <Row k="Courriel admin configuré" v={diag.env_admin_email} />
                  <Row k="Mot de passe de configuration" v={`${diag.env_admin_password_length} caractères`} />
                  <Row k="Compte admin aligné sur la configuration" v={diag.admin_password_matches_env ? "oui" : "non"} />
                  <Row k="Admin en base" v={diag.admin_email_in_db || "—"} />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Si le mot de passe de configuration ne correspond pas à celui que vous voulez, changez-le
                  avec le bouton « Mot de passe » de la barre latérale : votre choix devient définitif et
                  ne sera plus remplacé lors des mises en ligne.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <h3 className="font-display font-semibold flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" /> Réinitialiser toutes les données
              </h3>
              <p className="text-sm text-red-700/90 mt-1">
                Efface définitivement compagnies, garages, routes, chauffeurs, autobus, écoles, assignations,
                horaires, feuilles de temps, calendriers, alertes, positions GPS, journal et TOUS les autres
                comptes. Votre compte administrateur et les règles de sécurité sont conservés.
              </p>
              <div className="grid md:grid-cols-3 gap-3 items-end mt-4">
                <div><Label className="text-xs">Votre mot de passe</Label>
                  <Input type="password" className="mt-1 h-9" value={reset.password} data-testid="reset-password"
                    onChange={(e) => setReset({ ...reset, password: e.target.value })} /></div>
                <div><Label className="text-xs">Tapez REINITIALISER</Label>
                  <Input className="mt-1 h-9" value={reset.confirm} data-testid="reset-confirm"
                    onChange={(e) => setReset({ ...reset, confirm: e.target.value })} /></div>
                <Button variant="destructive" disabled={resetBusy} data-testid="reset-submit"
                  onClick={async () => {
                    setResetBusy(true);
                    try {
                      const { data } = await api.post("/admin/factory-reset", reset);
                      const n = Object.values(data.deleted).reduce((a, b) => a + b, 0);
                      toast.success(`${n} élément(s) supprimé(s) — environnement remis à neuf`);
                      setReset({ password: "", confirm: "" });
                      load();
                      api.get("/auth/bootstrap-status").then((r) => setDiag(r.data)).catch(() => {});
                    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                    finally { setResetBusy(false); }
                  }}>
                  {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Réinitialiser"}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* ---------- Audit ---------- */}
          <TabsContent value="audit">
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden max-h-[70vh] overflow-y-auto">
              {logs.map((l) => (
                <div key={l.id} className="px-5 py-2.5 bg-card grid grid-cols-12 gap-3 text-sm">
                  <div className="col-span-3 text-xs text-muted-foreground font-mono">{new Date(l.timestamp).toLocaleString("fr-CA")}</div>
                  <div className="col-span-3">{l.user_email || "système"}</div>
                  <div className="col-span-2"><Badge variant="outline">{l.action}</Badge></div>
                  <div className="col-span-4 text-xs text-muted-foreground">{l.entity} {l.entity_id?.slice(0, 8)}</div>
                </div>
              ))}
              {!logs.length && <div className="p-6 text-sm text-muted-foreground">Aucun journal.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LogoCard({ scope, title, hint }) {
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get(`/admin/branding?scope=${scope}`)
    .then((r) => setLogo(r.data.logo || null)).catch(() => {});
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [scope]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/branding?scope=${scope}`, fd);
      setLogo(data.logo);
      notifyLogoChanged();
      toast.success("Logo enregistré");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/branding?scope=${scope}`);
      setLogo(null);
      notifyLogoChanged();
      toast.success("Logo retiré");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="border border-border rounded-lg p-5 bg-card" data-testid={`logo-card-${scope}`}>
      <div className="font-display font-semibold">{title}</div>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
      <div className="mt-4 h-28 rounded-md border border-dashed border-border bg-secondary flex items-center justify-center overflow-hidden">
        {logo
          ? <img src={logo} alt={title} className="max-h-24 max-w-full object-contain" data-testid={`logo-preview-${scope}`} />
          : <span className="text-xs text-muted-foreground">Aucun logo téléversé</span>}
      </div>
      <div className="flex items-center gap-2 mt-4">
        <label className="inline-flex">
          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
            onChange={upload} data-testid={`logo-input-${scope}`} />
          <Button asChild size="sm" disabled={busy}>
            <span>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Téléverser</>}</span>
          </Button>
        </label>
        {logo && <Button size="sm" variant="outline" onClick={remove} data-testid={`logo-remove-${scope}`}>
          <Trash2 className="w-4 h-4 mr-1" /> Retirer</Button>}
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">PNG, JPEG, WEBP ou SVG · 900 Ko maximum · fond transparent recommandé.</div>
    </div>
  );
}


function Row({ k, v }) {  return <div className="flex justify-between gap-4 border-b border-border/60 pb-1">
    <span className="text-muted-foreground">{k}</span><span className="font-mono">{v}</span>
  </div>;
}

function Rows({ items, render, onDel, testid }) {
  if (!items.length) return <div className="text-sm text-muted-foreground py-8 text-center border border-dashed rounded-lg">Aucun élément.</div>;
  return (
    <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
      {items.map((it, i) => (
        <div key={it.id} className="grid grid-cols-5 gap-3 px-5 py-3 items-center text-sm bg-card" data-testid={`${testid}-row-${i}`}>
          {render(it).map((c, j) => <div key={j} className={j === 0 ? "font-medium" : "text-muted-foreground"}>{c}</div>)}
          <div className="text-right"><Button size="sm" variant="ghost" onClick={() => onDel(it)} data-testid={`${testid}-del-${i}`}><Trash2 className="w-4 h-4" /></Button></div>
        </div>
      ))}
    </div>
  );
}
